/**
 * Dashboard Component
 * Main landing page — real data overview + full navigation
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getServerSetupWarnings } from '../utils/serverSetupWarnings';
import DuplicateVmsWidget from './DuplicateVmsWidget';
import SecurityDashboard from './SecurityDashboard';
import './Dashboard.css';

const API_BASE = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '').replace(/\/$/, '');

function AdvisoryBanner({ summary, navigate }) {
  if (!summary?.count) return null;

  const items = summary.servers || [];
  const first = items[0];
  return (
    <div className="db-advisory-banner">
      <div className="db-advisory-copy">
        <div className="db-advisory-eyebrow">Dependencias de inventario</div>
        <h2>{first?.title || 'Hay servidores que requieren revisión manual de storage'}</h2>
        <p>
          {summary.count} servidor{summary.count === 1 ? '' : 'es'} requieren CrystalDiskInfo porque el agente solo ha podido ver el volumen RAID lógico y no los discos físicos miembro.
        </p>
      </div>
      <div className="db-advisory-list">
        {items.slice(0, 5).map((item) => (
          <button
            key={`${item.server_id}-${item.code}`}
            className="db-advisory-item"
            onClick={() => navigate('/infrastructure/servers', { state: { openId: item.server_id } })}
            type="button"
          >
            <span className="db-advisory-item-name">{item.server_name}</span>
            <span className="db-advisory-item-msg">{item.message}</span>
          </button>
        ))}
        {summary.count > 5 && (
          <button
            className="db-advisory-more"
            onClick={() => navigate('/infrastructure/servers')}
            type="button"
          >
            Ver los {summary.count} servidores afectados
          </button>
        )}
      </div>
    </div>
  );
}

function relativeTime(isoValue) {
  if (!isoValue) return 'sin fecha';
  const diffMs = Date.now() - new Date(isoValue).getTime();
  const minutes = Math.max(1, Math.round(diffMs / 60000));
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} d`;
}

// Cuenta atrás de una caducidad: los avisos de licencia no son eventos del
// pasado, así que `relativeTime` daría una fecha absurda.
function expiryCountdown(days) {
  if (days === 0) return 'caduca hoy';
  if (days < 0) return `caducó hace ${Math.abs(days)} d`;
  return `quedan ${days} d`;
}

function OfflineAgentsWidget({ payload, navigate }) {
  const items = payload?.servers || [];

  return (
    <div className="db-panel db-offline-panel">
      <div className="db-panel-header">
        <div>
          <div className="db-panel-eyebrow">Supervisión de agentes</div>
          <h3>Agentes offline</h3>
        </div>
        <span className="db-panel-pill db-panel-pill--critical">{payload?.count || 0}</span>
      </div>
      {items.length === 0 ? (
        <div className="db-panel-empty">Todos los agentes asociados reportan correctamente.</div>
      ) : (
        <div className="db-feed-list">
          {items.map((item) => (
            <button
              key={`offline-${item.server_id}`}
              className="db-feed-item db-feed-item--critical"
              onClick={() => navigate('/infrastructure/servers', { state: { openId: item.server_id } })}
              type="button"
            >
              <span className="db-feed-item-title">{item.server_name}</span>
              <span className="db-feed-item-copy">
                {item.location_name || 'Sin ubicación'}{item.rack_name ? ` · ${item.rack_name}` : ''}
              </span>
              <span className="db-feed-item-meta">Último heartbeat {relativeTime(item.last_seen_at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AlertsFeed({ payload, navigate }) {
  const items = payload?.items || [];

  return (
    <div className="db-panel db-alerts-panel">
      <div className="db-panel-header">
        <div>
          <div className="db-panel-eyebrow">Centro de eventos</div>
          <h3>Feed de avisos</h3>
        </div>
        <div className="db-feed-summary">
          <span className="db-panel-pill db-panel-pill--critical">{payload?.summary?.critical || 0}</span>
          <span className="db-panel-pill db-panel-pill--warning">{payload?.summary?.warning || 0}</span>
          <span className="db-panel-pill db-panel-pill--info">{payload?.summary?.info || 0}</span>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="db-panel-empty">No hay eventos recientes para esta empresa.</div>
      ) : (
        <div className="db-feed-list">
          {items.map((item, index) => (
            <button
              key={`${item.kind || 'event'}-${item.server_id || 'global'}-${item.timestamp || index}`}
              className={`db-feed-item db-feed-item--${item.severity || 'info'}`}
              onClick={() => navigate(item.route || '/infrastructure/servers', item.server_id ? { state: { openId: item.server_id } } : undefined)}
              type="button"
            >
              <span className="db-feed-item-title">{item.title}</span>
              <span className="db-feed-item-copy">{item.server_name ? `${item.server_name} · ` : ''}{item.message}</span>
              <span className="db-feed-item-meta">
                {typeof item.days === 'number' ? expiryCountdown(item.days) : relativeTime(item.timestamp)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SetupWarningsWidget({ payload, navigate }) {
  const items = payload?.items || [];

  return (
    <div className="db-panel db-alerts-panel">
      <div className="db-panel-header">
        <div>
          <div className="db-panel-eyebrow">Checklist de configuración</div>
          <h3>Pendientes en servidores</h3>
        </div>
        <span className="db-panel-pill db-panel-pill--warning">{payload?.count || 0}</span>
      </div>
      {items.length === 0 ? (
        <div className="db-panel-empty">No hay pendientes de ubicación, roles o BMC.</div>
      ) : (
        <div className="db-feed-list">
          {items.map((item) => (
            <button
              key={`setup-${item.server_id}`}
              className="db-feed-item db-feed-item--warning"
              onClick={() => navigate('/infrastructure/servers', { state: { openId: item.server_id } })}
              type="button"
            >
              <span className="db-feed-item-title">{item.server_name}</span>
              <span className="db-feed-item-copy">{item.missing_labels.join(' · ')}</span>
              <span className="db-feed-item-meta">{item.location_name || 'Sin ubicación'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VmNoAgentWidget({ payload, navigate }) {
  const items = payload?.items || [];
  return (
    <div className="db-panel db-alerts-panel">
      <div className="db-panel-header">
        <div>
          <div className="db-panel-eyebrow">Virtualización</div>
          <h3>VMs sin agente</h3>
        </div>
        <span className="db-panel-pill db-panel-pill--warning">{payload?.count || 0}</span>
      </div>
      {items.length === 0 ? (
        <div className="db-panel-empty">Todas las VMs reportan agente guest o están fuera de alcance.</div>
      ) : (
        <div className="db-feed-list">
          {items.map((item) => (
            <button
              key={`vm-no-agent-${item.id}`}
              className="db-feed-item db-feed-item--warning"
              onClick={() => navigate('/virt/vms')}
              type="button"
            >
              <span className="db-feed-item-title">{item.name}</span>
              <span className="db-feed-item-copy">{item.hypervisor_name || item.hosting_provider_label || (item.hosting_hidden ? 'Infraestructura externa' : 'Sin hipervisor')} · {item.power_state || 'unknown'}</span>
              <span className="db-feed-item-meta">{item.os_name || 'SO desconocido'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Occupancy bar ─────────────────────────────────────────────────────────────
function OccupancyBar({ pct }) {
  const p = Math.min(100, Math.max(0, pct ?? 0));
  const color = p >= 85 ? '#ef4444' : p >= 65 ? '#f59e0b' : '#22c55e';
  return (
    <div className="db-occ-wrap">
      <div className="db-occ-track">
        <div className="db-occ-fill" style={{ width: `${p}%`, background: color }} />
      </div>
      <span className="db-occ-pct" style={{ color }}>{p}%</span>
    </div>
  );
}

function MetricSummaryCard({ eyebrow, title, count, tone = 'info', secondary, onClick, expanded = false }) {
  return (
    <button type="button" className={`db-summary-card db-summary-card--${tone} ${expanded ? 'is-expanded' : ''}`} onClick={onClick}>
      <div className="db-summary-card-topline">{eyebrow}</div>
      <div className="db-summary-card-main">
        <span className="db-summary-card-title">{title}</span>
        <span className={`db-summary-card-count db-summary-card-count--${tone}`}>{count ?? 0}</span>
      </div>
      {secondary && <div className="db-summary-card-secondary">{secondary}</div>}
      <div className="db-summary-card-action">{expanded ? 'Ocultar detalle' : 'Ver detalle'}</div>
    </button>
  );
}

function ExpandableMetricCard({ eyebrow, title, count, tone, secondary, expanded, onToggle, children }) {
  return (
    <div className={`db-summary-card-shell db-summary-card-shell--${tone} ${expanded ? 'is-expanded' : ''}`}>
      <MetricSummaryCard
        eyebrow={eyebrow}
        title={title}
        count={count}
        tone={tone}
        secondary={secondary}
        onClick={onToggle}
        expanded={expanded}
      />
      {expanded && <div className="db-summary-card-detail">{children}</div>}
    </div>
  );
}

function MetricSnapshotCard({ eyebrow, title, value, secondary }) {
  return (
    <div className="db-summary-card db-summary-card--snapshot">
      <div className="db-summary-card-topline">{eyebrow}</div>
      <div className="db-summary-card-main">
        <span className="db-summary-card-title">{title}</span>
        <span className="db-summary-card-count db-summary-card-count--snapshot">{value}</span>
      </div>
      {secondary && <div className="db-summary-card-secondary">{secondary}</div>}
      <div className="db-summary-card-action">Vista general</div>
    </div>
  );
}

// ── Contenido de fondo del panel ─────────────────────────────────────────────
//
// Las tarjetas de arriba solo aparecen cuando hay algo que contar (avisos,
// agentes caídos, checklist...). Un día tranquilo dejaba el panel vacío: eso es
// lo que arreglan estos tres bloques, que se alimentan de inventario y siempre
// tienen datos.

/** Inventario por tipo de CI, agrupado y con acceso directo a cada listado. */
function InventoryPanel({ inventory, onNavigate }) {
  if (!inventory) return null;

  const groups = [
    {
      label: 'Infraestructura',
      items: [
        { key: 'servers', label: 'Servidores', route: '/infrastructure/servers' },
        { key: 'switches', label: 'Switches', route: '/infrastructure/switches' },
        { key: 'storage_arrays', label: 'Cabinas', route: '/infrastructure/storage-arrays' },
        { key: 'pdus', label: 'PDUs', route: '/infrastructure/pdus' },
        { key: 'ups', label: 'SAI', route: '/infrastructure/ups' },
        { key: 'patch_panels', label: 'Patch panels', route: '/infrastructure/patch-panels' },
      ],
    },
    {
      label: 'Red y conectividad',
      items: [
        { key: 'firewalls', label: 'Firewalls', route: '/infrastructure/firewalls' },
        { key: 'routers', label: 'Routers', route: '/infrastructure/routers' },
        { key: 'wifi_controllers', label: 'Controladoras WiFi', route: '/wifi/controllers' },
        { key: 'wifi_aps', label: 'Puntos de acceso', route: '/wifi/aps' },
      ],
    },
    {
      label: 'Virtualización',
      items: [
        { key: 'virt_clusters', label: 'Clusters', route: '/virt/clusters' },
        { key: 'hypervisors', label: 'Hipervisores', route: '/virt/hypervisors' },
        { key: 'vms', label: 'Máquinas virtuales', route: '/virt/vms' },
      ],
    },
    {
      label: 'Puestos y servicios',
      items: [
        { key: 'workstations', label: 'Estaciones', route: '/infrastructure/workstations' },
        { key: 'printers', label: 'Impresoras', route: '/infrastructure/printers' },
        { key: 'services', label: 'Servicios', route: '/services' },
        { key: 'databases', label: 'Instancias BD', route: '/apps/databases' },
        { key: 'instance_databases', label: 'Bases alojadas', route: '/apps/databases' },
        { key: 'web_sites', label: 'Sitios web', route: '/apps/web-sites' },
        { key: 'licenses', label: 'Licencias', route: '/apps/licenses' },
      ],
    },
  ];

  return (
    <section className="db-block db-block--inventory">
      <header className="db-block-head">
        <h2 className="db-block-title">Inventario</h2>
        <span className="db-block-hint">Pulsa un tipo para abrir su listado</span>
      </header>
      <div className="db-inv-groups">
        {groups.map((group) => (
          <div key={group.label} className="db-inv-group">
            <span className="db-inv-group-label">{group.label}</span>
            <div className="db-inv-tiles">
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="db-inv-tile"
                  onClick={() => item.route && onNavigate(item.route)}
                  disabled={!item.route}
                >
                  <span className="db-inv-value">{inventory[item.key] ?? 0}</span>
                  <span className="db-inv-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Ocupación de cada rack. Un global del 24 % no dice dónde está el hueco. */
function RacksOccupancyPanel({ racks, onNavigate }) {
  if (!racks || !racks.length) return null;

  const toneClass = (pct) => {
    if (pct >= 85) return 'is-critical';
    if (pct >= 70) return 'is-warning';
    return '';
  };

  const usedU = racks.reduce((acc, rack) => acc + rack.used_u, 0);
  const totalU = racks.reduce((acc, rack) => acc + rack.total_u, 0);

  return (
    <section className="db-block db-block--racks">
      <header className="db-block-head">
        <h2 className="db-block-title">Ocupación por rack</h2>
        <span className="db-block-hint">
          {usedU} de {totalU} U en {racks.length} racks
        </span>
      </header>
      <div className="db-racks">
        {racks.map((rack) => (
          <button
            key={rack.id}
            type="button"
            className="db-rack"
            onClick={() => onNavigate(`/infrastructure/rack/${rack.id}`)}
          >
            <span className="db-rack-name">
              {rack.name}
              {rack.location && <em>{rack.location}</em>}
            </span>
            <span className={`db-rack-bar ${toneClass(rack.percent)}`}>
              <i style={{ width: `${Math.min(100, rack.percent)}%` }} />
            </span>
            <span className="db-rack-pct">
              {rack.percent}%
              <em>
                {rack.used_u}/{rack.total_u} U
              </em>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Direccionamiento IP: la pregunta real es si queda margen para una subred más. */
function IpamPanel({ ipam, onNavigate }) {
  if (!ipam || !ipam.ips) return null;

  const free = Math.max(0, ipam.ips - ipam.ips_used);
  const tight = ipam.percent_used >= 90;

  return (
    <section className="db-block db-block--ipam">
      <header className="db-block-head">
        <h2 className="db-block-title">Direccionamiento IP</h2>
        <span className="db-block-hint">{ipam.ips} direcciones inventariadas</span>
      </header>

      <div className="db-ipam-figure">
        <span className={`db-ipam-value ${tight ? 'is-critical' : ''}`}>{ipam.percent_used}%</span>
        <span className="db-ipam-caption">en uso</span>
      </div>

      <span className="db-ipam-bar">
        <i
          className={tight ? 'is-critical' : ''}
          style={{ width: `${Math.min(100, ipam.percent_used)}%` }}
        />
      </span>

      <div className="db-ipam-facts">
        <button type="button" onClick={() => onNavigate('/networks/blocks')}>
          <b>{ipam.blocks}</b> bloques
        </button>
        <button type="button" onClick={() => onNavigate('/networks/subnets')}>
          <b>{ipam.subnets}</b> subredes
        </button>
        <button type="button" onClick={() => onNavigate('/networks/vlans')}>
          <b>{ipam.vlans}</b> VLANs
        </button>
        <button type="button" onClick={() => onNavigate('/networks/ips')}>
          <b>{free}</b> libres
        </button>
      </div>

      {tight && (
        <p className="db-ipam-alert">
          Queda poco margen: {free} de {ipam.ips} direcciones libres. Conviene revisar
          las subredes antes de crear direccionamiento nuevo.
        </p>
      )}
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * Renovaciones próximas (dominios DNS que caducan en 30 días o ya vencieron).
 *
 * El aviso por defecto es a un mes, que es lo que pide el usuario: tiempo de
 * sobra para renovar sin prisas. Se pinta como lista corta con enlace al DNS.
 * Si no hay nada, no ocupa sitio.
 */
function RenewalsPanel({ renewals, onNavigate }) {
  if (!renewals || !renewals.count) return null;
  const items = renewals.items || [];
  return (
    <div className="db-renewals">
      <div className="db-renewals-head">
        <h3>Renovaciones en {renewals.days} días</h3>
        <span className="list-badge list-badge--warn">{renewals.count}</span>
        {renewals.expired > 0 && (
          <span className="list-badge list-badge--off">{renewals.expired} vencidos</span>
        )}
        <span className="dv-topbar-spacer" style={{ flex: 1 }} />
        <button type="button" className="sf2-link" onClick={() => onNavigate('/dns')}>
          Ver DNS →
        </button>
      </div>
      <ul>
        {items.slice(0, 6).map((item) => (
          <li key={`${item.kind}-${item.id}`} className={`db-renewal${item.days_left < 0 ? ' is-expired' : ''}`}>
            <span className={`list-badge ${item.kind === 'certificate' ? 'list-badge--accent' : 'list-badge--muted'}`}>
              {item.kind === 'certificate' ? 'TLS' : 'DNS'}
            </span>
            <strong>{item.name}</strong>
            {item.company_name ? <span className="list-hint">· {item.company_name}</span> : null}
            <span className="list-hint">{item.expires_at}</span>
            <span className="db-renewal-days">
              {item.days_left < 0 ? `vencido hace ${Math.abs(item.days_left)} d` : `en ${item.days_left} d`}
            </span>
          </li>
        ))}
      </ul>
      {items.length > 6 && (
        <p className="list-hint" style={{ margin: '6px 0 0' }}>y {items.length - 6} más…</p>
      )}
    </div>
  );
}

const Dashboard = ({ companyId }) => {
  const navigate = useNavigate();
  const [kpis,    setKpis]    = useState(null);
  const [advisories, setAdvisories] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [setupWarnings, setSetupWarnings] = useState(null);
  const [vmNoAgent, setVmNoAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  // Dos pestañas: «Resumen» (lo que hay) y «Seguridad» (cómo está).
  const [tab, setTab] = useState(['resumen', 'seguridad'].includes(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null,
  ) ? new URLSearchParams(window.location.search).get('tab') : 'resumen');
  const [expandedMetrics, setExpandedMetrics] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboard_expanded_metrics_v1');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleMetric = useCallback((metricKey) => {
    setExpandedMetrics((prev) => {
      const next = { ...prev, [metricKey]: !prev[metricKey] };
      try {
        localStorage.setItem('dashboard_expanded_metrics_v1', JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!companyId) { setKpis(null); setAdvisories(null); setAlerts(null); setSetupWarnings(null); setVmNoAgent(null); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const [kpiRes, advisoryRes, alertRes, serversRes, vmNoAgentRes] = await Promise.all([
        fetch(`${API_BASE}/api/dashboard/kpis?company_id=${companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        }).then(r => r.ok ? r.json() : Promise.reject(r.statusText)),
        fetch(`${API_BASE}/api/dashboard/storage-advisories?company_id=${companyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        }).then(r => r.ok ? r.json() : Promise.reject(r.statusText)),
        api.getDashboardAlertsFeed(companyId, 8).catch(() => null),
        api.getServers(companyId).catch(() => ({ data: [] })),
        api.getVMs(companyId, { agent_status: 'no-agent' }).catch(() => ({ data: [] })),
      ]);
      const servers = Array.isArray(serversRes?.data) ? serversRes.data : [];
      const vmNoAgentItems = (vmNoAgentRes?.data || []).slice(0, 8);
      const setupItems = servers
        .map((srv) => {
          const warnings = getServerSetupWarnings(srv);
          if (!warnings.length) return null;
          return {
            server_id: srv.id,
            server_name: srv.name || `Servidor #${srv.id}`,
            location_name: srv.location_name,
            missing_labels: warnings.map((w) => w.title),
          };
        })
        .filter(Boolean)
        .slice(0, 8);

      setKpis(kpiRes);
      setAdvisories(advisoryRes);
      setAlerts(alertRes);
      setSetupWarnings({
        count: setupItems.length,
        items: setupItems,
      });
      setVmNoAgent({
        count: Array.isArray(vmNoAgentRes?.data) ? vmNoAgentRes.data.length : 0,
        items: vmNoAgentItems,
      });
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const occupancyPercent = Math.min(100, Math.max(0, kpis?.occupancy_percent ?? 0));
  const alertsTotal = (alerts?.summary?.critical || 0) + (alerts?.summary?.warning || 0) + (alerts?.summary?.info || 0);

  const summaryCards = [
    {
      key: 'advisories',
      eyebrow: 'Dependencias de inventario',
      title: 'Storage por revisar',
      count: advisories?.count || 0,
      displayCount: advisories?.count || 0,
      tone: 'warning',
      secondary: 'Servidores con revisión manual requerida',
    },
    {
      key: 'offline',
      eyebrow: 'Supervisión de agentes',
      title: 'Agentes offline',
      count: alerts?.offline_agents?.count || 0,
      displayCount: alerts?.offline_agents?.count || 0,
      tone: 'critical',
      secondary: 'Estado de heartbeat de agentes',
    },
    {
      key: 'alerts',
      eyebrow: 'Centro de eventos',
      title: 'Feed de avisos',
      count: alertsTotal,
      displayCount: alertsTotal,
      tone: 'info',
      secondary: `${alerts?.summary?.critical || 0} críticos · ${alerts?.summary?.warning || 0} warning`,
    },
    {
      key: 'setup',
      eyebrow: 'Checklist de configuración',
      title: 'Pendientes en servidores',
      count: setupWarnings?.count || 0,
      displayCount: setupWarnings?.count || 0,
      tone: 'warning',
      secondary: 'Roles, ubicación o BMC incompletos',
    },
    {
      key: 'vmNoAgent',
      eyebrow: 'Virtualización',
      title: 'VMs sin agente',
      count: vmNoAgent?.count || 0,
      displayCount: vmNoAgent?.count || 0,
      tone: 'warning',
      secondary: 'Inventario guest no reportado',
    },
    {
      key: 'occupancy',
      eyebrow: 'Capacidad',
      title: 'Ocupación de racks',
      count: occupancyPercent,
      displayCount: `${occupancyPercent}%`,
      tone: 'info',
      secondary: 'Capacidad actual de racks',
    },
  ];

  const activeSummaryCards = summaryCards.filter((card) => card.count > 0);
  const inventorySnapshot = [
    {
      key: 'locations',
      eyebrow: 'Inventario base',
      title: 'Ubicaciones',
      value: kpis?.locations ?? 0,
      secondary: 'Sedes y edificios registrados',
    },
    {
      key: 'racks',
      eyebrow: 'Inventario base',
      title: 'Racks',
      value: kpis?.racks ?? 0,
      secondary: 'Bastidores físicos inventariados',
    },
    {
      key: 'devices',
      eyebrow: 'Inventario base',
      title: 'Dispositivos',
      value: kpis?.devices ?? 0,
      secondary: 'Elementos de rack detectados',
    },
    {
      key: 'changes',
      eyebrow: 'Actividad',
      title: 'Cambios 24h',
      value: kpis?.recent_changes ?? 0,
      secondary: 'Eventos de auditoría recientes',
    },
  ];

  return (
    <div className="dashboard">
      {/* ── Header ── */}
      <div className="dashboard-header dashboard-header--hero">
        <div>
          <p className="dashboard-eyebrow">Configuration Management Database</p>
          <h1 className="dashboard-title">Centro de control</h1>
          <p className="dashboard-subtitle">
            Inventario, alertas y postura de seguridad en una sola pantalla.
          </p>
          {companyId && kpis && (
            <div className="dashboard-hero-chips">
              <span className="list-badge list-badge--accent">{kpis.locations ?? 0} ubicaciones</span>
              <span className="list-badge list-badge--accent">{kpis.racks ?? 0} racks</span>
              <span className="list-badge list-badge--accent">{kpis.devices ?? 0} dispositivos</span>
              <span className="list-badge list-badge--muted">{kpis.recent_changes ?? 0} cambios 24 h</span>
            </div>
          )}
        </div>
        <div className="dashboard-header-actions">
          <button className="db-refresh-btn" onClick={loadData} disabled={loading} title="Recargar datos">
            {loading ? '⌛' : '↺'} Actualizar
          </button>
        </div>
      </div>

      {/* ── Pestañas ── */}
      <div className="db-tabs" role="tablist" aria-label="Vistas del inicio">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'resumen'}
          className={`db-tab${tab === 'resumen' ? ' is-active' : ''}`}
          onClick={() => setTab('resumen')}
        >
          Resumen
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'seguridad'}
          className={`db-tab${tab === 'seguridad' ? ' is-active' : ''}`}
          onClick={() => setTab('seguridad')}
        >
          Seguridad
        </button>
      </div>

      {/* ── No company ── */}
      {!companyId && (
        <div className="db-no-company">
          <div className="db-no-company-icon">🏢</div>
          <h3>Selecciona una empresa</h3>
          <p>Usa el selector en la barra superior para ver los KPIs y las métricas de cada empresa.</p>
        </div>
      )}

      {companyId && tab === 'seguridad' && <SecurityDashboard companyId={companyId} />}

      {/* ── KPIs ── */}
      {companyId && tab === 'resumen' && (
        <>
        <div className="db-kpi-section">
          {error && <div className="db-error">Error al cargar datos: {error}</div>}
          {loading && !kpis && <div className="db-loading">Cargando métricas…</div>}

          {/* Duplicate VM banner — shown above everything else */}
          <DuplicateVmsWidget companyId={companyId} />

          {activeSummaryCards.length > 0 && (
            <div className="db-summary-grid">
              {activeSummaryCards.map((card) => (
                <ExpandableMetricCard
                  key={card.key}
                  eyebrow={card.eyebrow}
                  title={card.title}
                  count={card.displayCount}
                  tone={card.tone}
                  secondary={card.secondary}
                  onToggle={() => toggleMetric(card.key)}
                  expanded={!!expandedMetrics[card.key]}
                >
                  {card.key === 'advisories' && <AdvisoryBanner summary={advisories} navigate={navigate} />}
                  {card.key === 'offline' && <OfflineAgentsWidget payload={alerts?.offline_agents} navigate={navigate} />}
                  {card.key === 'alerts' && <AlertsFeed payload={alerts} navigate={navigate} />}
                  {card.key === 'setup' && <SetupWarningsWidget payload={setupWarnings} navigate={navigate} />}
                  {card.key === 'vmNoAgent' && <VmNoAgentWidget payload={vmNoAgent} navigate={navigate} />}
                  {card.key === 'occupancy' && (
                    <div className="db-occ-section db-occ-section--card">
                      <span className="db-occ-title">Ocupacion de racks</span>
                      <OccupancyBar pct={kpis?.occupancy_percent} />
                    </div>
                  )}
                </ExpandableMetricCard>
              ))}
            </div>
          )}

          {activeSummaryCards.length === 0 && !loading && !error && kpis && (
            <>
              <div className="db-summary-grid db-summary-grid--snapshot">
                {inventorySnapshot.map((card) => (
                  <MetricSnapshotCard
                    key={card.key}
                    eyebrow={card.eyebrow}
                    title={card.title}
                    value={card.value}
                    secondary={card.secondary}
                  />
                ))}
              </div>
              <div className="db-no-issues-card">Sin incidencias activas. Mostrando fotografía general del inventario.</div>
            </>
          )}

        </div>

        {/* Contenido de fondo del panel: no depende de que haya incidencias,
            así que un día tranquilo no deja la pantalla vacía. */}
        <div className="db-content-grid">
          <InventoryPanel inventory={kpis?.inventory} onNavigate={navigate} />
          <IpamPanel ipam={kpis?.ipam} onNavigate={navigate} />
        </div>
        <RenewalsPanel renewals={kpis?.renewals} onNavigate={navigate} />
        <RacksOccupancyPanel racks={kpis?.racks_detail} onNavigate={navigate} />
        </>
      )}
    </div>
  );
};

export default Dashboard;

